import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchOrderMenus, OrderCategory, addCart, fetchCart, updateCartItemQuantity as updateCartItemQuantityApi, checkoutInOrder } from './orderApi';

interface MenuOption {
  menuOptionId: string;
  menuOptionName: string;
  price: number | null;
  priceType: 'ADDITIONAL' | 'DISCOUNT' | null;
}

interface MenuOptionGroup {
  menuOptionGroupId: string;
  /** 必須グループかどうか。BE 未対応レスポンスでは欠落するため、欠落時は true（必須）として扱う */
  required?: boolean;
  menuOptions: MenuOption[];
}

interface MenuOptionGroupItem {
  menuId: string;
  menuOptionGroup: MenuOptionGroup;
}

export interface OrderState {
  categories: OrderCategory[];
  storeName: string | null;
  /** オーダー画面テンプレート（IMAGE=画像つき / TEXT=画像なし）。BE ADR 0068。 */
  orderMenuTemplate: 'IMAGE' | 'TEXT';
  loading: boolean;
  error: string | null;
  carts: CartItem[];
  menuOptions: {
    menuOptionGroups: MenuOptionGroupItem[];
  };
}

interface CartItem {
  id: string;
  menuId: string;
  menuOptionId: string | null;
  name: string;
  price: number;
  quantity: number;
  imageUrl: string;
}

const initialState: OrderState = {
  categories: [],
  storeName: null,
  orderMenuTemplate: 'IMAGE',
  loading: false,
  error: null,
  carts: [],
  menuOptions: {
    menuOptionGroups: [],
  },
};

const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    receiveCartUpdate: (state, action: PayloadAction<CartItem[]>) => {
      state.carts = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrderMenus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrderMenus.fulfilled, (state, action) => {
        state.categories = action.payload.menuCategories;
        state.storeName = action.payload.storeName;
        state.orderMenuTemplate = action.payload.orderMenuTemplate === 'TEXT' ? 'TEXT' : 'IMAGE';
        state.menuOptions = action.payload.menuOptions;
        state.loading = false;
      })
      .addCase(fetchOrderMenus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(addCart.pending, (state) => {
        // 送信中の状態をセット等
      })
      .addCase(addCart.fulfilled, (state, action) => {
        // stateの更新はfetchCartに任せる
      })
      .addCase(addCart.rejected, (state, action) => {
        // 失敗時のエラー処理
        state.error = action.payload as string;
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        const { items } = action.payload;
        // カートアイテムにメニュー情報をマッピング
        state.carts = items
          .map(item => {
            // 全カテゴリーから該当するメニューアイテムを検索
            const menuItem = state.categories
              .flatMap(category => category.items)
              .find(menu => menu.id === item.menuId);

            if (!menuItem) {
              console.error(`Menu item not found for id: ${item.menuId}`);
              return undefined;
            }

            return {
              id: item.id,
              menuId: item.menuId,
              menuOptionId: item.menuOptionId,
              quantity: item.quantity,
              name: menuItem.name,
              price: menuItem.price,
              imageUrl: menuItem.imageUrl,
            };
          })
          .filter((item): item is CartItem => item !== undefined);
      })
      .addCase(updateCartItemQuantityApi.fulfilled, (state, action) => {
        // stateの更新はfetchCartに任せる
      })
      .addCase(checkoutInOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkoutInOrder.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(checkoutInOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { receiveCartUpdate } = orderSlice.actions;
export default orderSlice.reducer;
